import { useMemo } from "react";
import { useQuery } from "shared/hooks/useApi";
import { OFFERS } from "utils/api/graphql/queries/offers";
import { useInsurancePlans } from "./useInsurancePlans";

export const offerWhereForPlans = (planIds) => ({
  OR: (planIds || []).filter(Boolean).map((id) => ({
    AND: [
      { offerInsurancePlans: { insurancePlan: { id } } },
      { offerInsurancePlans: { main: true } },
    ],
  })),
});

const upsellOffersWhere = { offerInsurancePlans: { main: false } };

const linkedPlanId = (link) =>
  (link && link.insurancePlan && link.insurancePlan.id) || null;

const linksOf = (offer, isMain) =>
  ((offer && offer.offerInsurancePlans) || []).filter(
    (link) => link && Boolean(link.main) === isMain && linkedPlanId(link),
  );

/**
 * The offer filter also narrows the links it returns, so the main plans come
 * from the first query and the reinforcements from the second one.
 */
export const upsellsByPlanFromOffers = (
  mainOffers,
  upsellOffers,
  parentPlanIds,
) => {
  const byPlan = {};
  (parentPlanIds || []).forEach((id) => {
    if (id) byPlan[id] = [];
  });
  const parentsByOffer = {};
  (mainOffers || []).forEach((offer) => {
    parentsByOffer[offer.id] = linksOf(offer, true)
      .map(linkedPlanId)
      .filter((id) => byPlan[id]);
  });
  (upsellOffers || []).forEach((offer) => {
    const upsells = linksOf(offer, false)
      .map((link) => ({ id: linkedPlanId(link), weight: link.weight }))
      .filter(({ id }) => !byPlan[id]);
    (parentsByOffer[offer.id] || []).forEach((planId) => {
      upsells.forEach((upsell) => {
        if (!byPlan[planId].some(({ id }) => id === upsell.id)) {
          byPlan[planId].push(upsell);
        }
      });
    });
  });
  return byPlan;
};

export const upsellIdsOf = (byPlan) => {
  const ids = [];
  Object.keys(byPlan || {}).forEach((planId) => {
    byPlan[planId].forEach(({ id }) => {
      if (ids.indexOf(id) === -1) ids.push(id);
    });
  });
  return ids;
};

const moduleOf = (upsell) => ({
  id: upsell.id,
  cost: upsell.cost,
  currency: upsell.currency,
  weight: upsell.weight,
  insurancePlan: {
    id: upsell.id,
    name: upsell.name,
    alias: upsell.alias,
    identifier: upsell.identifier,
  },
});

const modulePlanId = (module) =>
  (module && module.insurancePlan && module.insurancePlan.id) || null;

/**
 * `fields.upsells` keeps the modules picked under each main plan, the same
 * `{ [planId]: [{ cost, currency, weight, insurancePlan }] }` map as the
 * static journeys, so quotes and subscriptions read it unchanged.
 */
export const pickedUpsellsOf = (fields) => {
  const upsells = fields && fields.upsells;
  return upsells && typeof upsells === "object" && !Array.isArray(upsells)
    ? upsells
    : {};
};

export const toggleUpsellModule = (pickedUpsells, planId, upsell) => {
  const modules = (pickedUpsells && pickedUpsells[planId]) || [];
  const isPicked = modules.some((module) => modulePlanId(module) === upsell.id);
  return {
    ...pickedUpsells,
    [planId]: isPicked
      ? modules.filter((module) => modulePlanId(module) !== upsell.id)
      : [...modules, moduleOf(upsell)],
  };
};

/**
 * Serialized picks, so a memo can depend on the selection itself rather than
 * on the buffered fields object that is rebuilt on every keystroke.
 */
export const pickedUpsellKey = (pickedUpsells) =>
  Object.keys(pickedUpsells || {})
    .sort()
    .map((planId) =>
      [planId, ...(pickedUpsells[planId] || []).map(modulePlanId)].join(":"),
    )
    .join("|");

const pickedIdsFromKey = (key) => {
  const byPlan = {};
  (key || "").split("|").forEach((entry) => {
    const [planId, ...ids] = entry.split(":");
    if (planId) byPlan[planId] = ids.filter(Boolean);
  });
  return byPlan;
};

export const markPickedUpsells = (items, pickedKey) => {
  const pickedIdsByPlan = pickedIdsFromKey(pickedKey);
  return (items || []).map((item) => {
    if (!item || !Array.isArray(item.upsells)) return item;
    const pickedIds = pickedIdsByPlan[item.id] || [];
    return {
      ...item,
      upsells: item.upsells.map((upsell) => ({
        ...upsell,
        selected: pickedIds.indexOf(upsell.id) === -1 ? "false" : "true",
      })),
    };
  });
};

const offersOf = (data) => (data && data.offers && data.offers.data) || null;

// Both queries return the same offers with different links: a shared cache
// entry would let the second response overwrite the main links of the first.
const offersFetchPolicy = "no-cache";

const noUpsells = { upsellPlans: [], upsellsByPlan: null };

export const usePlanUpsells = (enabled, parentPlans, eligibilityRules) => {
  const parentKey = (parentPlans || [])
    .map((plan) => plan && plan.id)
    .filter(Boolean)
    .join(",");
  const parentPlanIds = useMemo(
    () => parentKey.split(",").filter(Boolean),
    [parentKey],
  );
  const { data: mainData } = useQuery(OFFERS, {
    variables: { where: offerWhereForPlans(parentPlanIds) },
    fetchPolicy: offersFetchPolicy,
    skip: !enabled || parentPlanIds.length === 0,
  });
  const mainOffers = offersOf(mainData);
  const offerIds = useMemo(
    () => (mainOffers || []).map((offer) => offer.id).filter(Boolean),
    [mainOffers],
  );
  const { data: upsellData } = useQuery(OFFERS, {
    variables: { where: upsellOffersWhere, isIn: { id: offerIds } },
    fetchPolicy: offersFetchPolicy,
    skip: !enabled || offerIds.length === 0,
  });
  const upsellOffers = offersOf(upsellData);
  const upsellsByPlan = useMemo(
    () =>
      enabled
        ? upsellsByPlanFromOffers(mainOffers, upsellOffers, parentPlanIds)
        : null,
    [enabled, mainOffers, upsellOffers, parentPlanIds],
  );
  const upsellIds = useMemo(() => upsellIdsOf(upsellsByPlan), [upsellsByPlan]);
  const { insurancePlans } = useInsurancePlans(
    Boolean(enabled && upsellIds.length),
    upsellIds,
    eligibilityRules,
  );
  if (!enabled) return noUpsells;
  return { upsellPlans: insurancePlans, upsellsByPlan };
};
